/**
 * ATT-3464 — client-side entry into the Google flow.
 *
 * Deliberately separate from `centralAuthHost.ts`: that module pulls in the
 * registry resolver (and through it the GraphQL client) because its server-side
 * validation needs them. The sign-in button needs none of that — only the
 * configured host name — so this stays a leaf module safe to import from a page.
 *
 * `process.env.NEXT_PUBLIC_CENTRAL_AUTH_HOST` is written out in full rather
 * than destructured: Next.js only inlines `NEXT_PUBLIC_*` at build time when it
 * can see the complete member expression in the source.
 */

import { signIn } from "next-auth/react";
import { trackCentralAuthClientEvent } from "./centralAuthTelemetry";

/** Normalize like the server does — lowercase, no scheme, no port, no trailing dot. */
function normalize(host: string): string {
  let s = host.trim().toLowerCase();
  if (s.startsWith("https://")) s = s.slice("https://".length);
  else if (s.startsWith("http://")) s = s.slice("http://".length);
  if (s.includes(":")) s = s.slice(0, s.indexOf(":"));
  if (s.endsWith(".")) s = s.slice(0, -1);
  return s;
}

/** The configured central auth host, or undefined when it is not configured. */
export function getCentralAuthHostClient(): string | undefined {
  const raw = process.env.NEXT_PUBLIC_CENTRAL_AUTH_HOST;
  const normalized = raw ? normalize(raw) : "";
  return normalized || undefined;
}

/**
 * The registrable domain a host must sit under for the central flow to work at
 * all — the central-host session cookie is scoped here, so a host outside it
 * could never read the session the flow produces.
 */
const SHARED_REGISTRABLE_DOMAIN = "attendly.com";

/**
 * Can this host use the central auth flow?
 *
 * Compares LABELS, not a substring, so `admin.attendly.com.evil.example` and
 * `evil-attendly.com` are not eligible. Mirrors `isSharedRegistrableDomain` in
 * `centralAuthHost.ts` (the server-side guard); duplicated rather than imported
 * because that module pulls the registry resolver and the GraphQL client, which
 * must not be dragged into the sign-in page bundle.
 */
export function isCentralAuthEligibleHost(host: string): boolean {
  const h = normalize(host);
  return h === SHARED_REGISTRABLE_DOMAIN || h.endsWith(`.${SHARED_REGISTRABLE_DOMAIN}`);
}

export function buildCentralAuthStartUrl(centralHost: string, originUrl: string): string {
  return `https://${centralHost}/auth/start?origin=${encodeURIComponent(originUrl)}`;
}

/**
 * Start the Google sign-in flow.
 *
 * With a central auth host configured, this is a top-level navigation to it —
 * which is the entire mechanism: next-auth derives the OAuth `redirect_uri`
 * from the request host, so the flow has to actually BE on the central host for
 * the redirect URI to be the single fixed one.
 *
 * Without one configured (local development, preview deployments), it falls
 * back to today's in-place `signIn("google")` so those environments keep
 * working unchanged — and so unsetting the env var is a working rollback
 * (design.md §D6/R9).
 */
export function startGoogleSignIn(callbackPath = "/"): void {
  const central = getCentralAuthHostClient();
  const currentHost = typeof window !== "undefined" ? normalize(window.location.host) : "";

  // Already on the central host (or none configured) → nothing to bounce to.
  if (!central || currentHost === central) {
    signIn("google", { callbackUrl: callbackPath });
    return;
  }

  // ELIGIBILITY, not security. A session minted at the central host is scoped to
  // `.attendly.com` and is simply invisible on any other registrable domain, so
  // bouncing a legacy-domain user there would sign them in somewhere they cannot
  // see — the server rejects the origin and they land on an error page. Those
  // hosts keep the per-host flow until ATT-3353 moves them (scope decision 2).
  //
  // A suffix check is the right tool here: the server still runs the full
  // four-check `validateOrigin`, so this is only deciding WHICH flow to start.
  if (!isCentralAuthEligibleHost(currentHost)) {
    signIn("google", { callbackUrl: callbackPath });
    return;
  }

  const originUrl = `${window.location.origin}${callbackPath.startsWith("/") ? callbackPath : "/"}`;
  trackCentralAuthClientEvent("auth_google_central_start", { host: currentHost });
  window.location.assign(buildCentralAuthStartUrl(central, originUrl));
}
